/**
 * Lightweight markdown to HTML renderer.
 * Supports: headers, bold, italic, bullets, numbered lists, horizontal rules, pipe tables.
 * No external dependencies.
 */

export function renderMarkdown(md: string): string {
  const lines = md.split("\n");
  const html: string[] = [];
  let inList = false;
  let inOrderedList = false;
  let inTable = false;
  let tableHeaderDone = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Pipe table detection: | col | col |
    if (line.trim().startsWith("|") && line.trim().endsWith("|")) {
      const cells = line.split("|").slice(1, -1).map((c) => c.trim());

      // Separator row: | --- | --- |
      if (cells.every((c) => /^[-:]+$/.test(c))) {
        tableHeaderDone = true;
        continue;
      }

      closeLists();

      if (!inTable) {
        html.push('<div class="overflow-x-auto my-3"><table class="w-full text-sm border-collapse">');
        inTable = true;
        tableHeaderDone = false;
        // First row is header
        html.push("<thead><tr>");
        for (const cell of cells) {
          html.push(`<th class="text-left px-3 py-2 border-b-2 border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wide">${inline(cell)}</th>`);
        }
        html.push("</tr></thead><tbody>");
        continue;
      }

      // Data row
      html.push("<tr>");
      for (const cell of cells) {
        html.push(`<td class="px-3 py-2 border-b border-slate-100 text-slate-700">${inline(cell)}</td>`);
      }
      html.push("</tr>");
      continue;
    }

    // Close table if we hit a non-table line
    if (inTable) {
      html.push("</tbody></table></div>");
      inTable = false;
      tableHeaderDone = false;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      closeLists();
      html.push('<hr class="my-4 border-slate-200" />');
      continue;
    }

    // Headers
    if (line.startsWith("##### ")) {
      closeLists();
      html.push(`<h5 class="text-sm font-semibold text-slate-800 mt-4 mb-1">${inline(line.slice(6))}</h5>`);
      continue;
    }
    if (line.startsWith("#### ")) {
      closeLists();
      html.push(`<h4 class="text-sm font-semibold text-slate-800 mt-5 mb-1">${inline(line.slice(5))}</h4>`);
      continue;
    }
    if (line.startsWith("### ")) {
      closeLists();
      html.push(`<h3 class="text-base font-semibold text-slate-900 mt-6 mb-2">${inline(line.slice(4))}</h3>`);
      continue;
    }
    if (line.startsWith("## ")) {
      closeLists();
      html.push(`<h2 class="text-lg font-semibold text-slate-900 mt-6 mb-2 pb-1 border-b border-slate-100">${inline(line.slice(3))}</h2>`);
      continue;
    }
    if (line.startsWith("# ")) {
      closeLists();
      html.push(`<h1 class="text-xl font-bold text-slate-900 mt-6 mb-3">${inline(line.slice(2))}</h1>`);
      continue;
    }

    // Unordered list (supports indented sub-items with 2-4 spaces)
    const ulMatch = line.match(/^(\s*)[*\-] (.+)$/);
    if (ulMatch) {
      if (inOrderedList) { html.push("</ol>"); inOrderedList = false; }
      if (!inList) { html.push('<ul class="list-disc list-outside ml-5 my-2 space-y-1">'); inList = true; }
      const indent = ulMatch[1].length >= 2;
      html.push(`<li class="text-sm text-slate-700 ${indent ? "ml-4" : ""}">${inline(ulMatch[2])}</li>`);
      continue;
    }

    // Ordered list
    if (/^\d+\. (.+)$/.test(line)) {
      if (inList) { html.push("</ul>"); inList = false; }
      if (!inOrderedList) { html.push('<ol class="list-decimal list-outside ml-5 my-2 space-y-1">'); inOrderedList = true; }
      const content = line.replace(/^\d+\. /, "");
      html.push(`<li class="text-sm text-slate-700">${inline(content)}</li>`);
      continue;
    }

    // Close lists if we hit a non-list line
    closeLists();

    // Empty line
    if (line.trim() === "") {
      continue;
    }

    // Paragraph
    html.push(`<p class="text-sm text-slate-700 my-2 leading-relaxed">${inline(line)}</p>`);
  }

  closeLists();
  if (inTable) { html.push("</tbody></table></div>"); }
  return html.join("\n");

  function closeLists() {
    if (inList) { html.push("</ul>"); inList = false; }
    if (inOrderedList) { html.push("</ol>"); inOrderedList = false; }
  }
}

/** Inline formatting: bold, italic, code, links */
function inline(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code class="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-800">$1</code>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-blue-600 hover:underline" target="_blank">$1</a>');
}
